import { useCallback, useEffect, useState } from 'react';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import { useBrowseQuery } from '../../hooks/useBrowseQuery';
import { readKeywordFromUrl } from '../../utils/urlSearchParam';
import { BulkSearchDialogState } from './BulkSearch';
import { keywordToBulkSearchValues } from './BulkSearch/prepareQueryValues';
import { SearchFilterBar } from './SearchFilterBar';

/**
 * AngularJS mount host for the React search bar on `/browse` until
 * LAX-10137 routes the full BrowsePage. `useBrowseQuery` writes keyword
 * and filter params through `$location.search`, which triggers
 * `$routeUpdate` → `itemSearchQueryChanged` → the existing ItemsList fetch.
 */
export function SearchFilterBarHost() {
    const {
        query,
        setQuery,
        activeFilters,
        setActiveFilter,
        removeFilter,
        applyBulkSearch,
    } = useBrowseQuery();
    const [bulkSearchDialogState, setBulkSearchDialogState] =
        useState<BulkSearchDialogState | null>(null);

    const handleKeywordChange = useCallback(
        (keyword: string) => {
            setQuery({ keyword });
        },
        [setQuery],
    );

    const handleDialogStateConsumed = useCallback(() => {
        setBulkSearchDialogState(null);
    }, []);

    useEffect(() => {
        try {
            const $rootScope = getRootScope(getInjector()) as {
                $on?: (name: string, listener: () => void) => () => void;
            };
            if (!$rootScope?.$on) {
                return undefined;
            }

            return $rootScope.$on('openBulkSearchOnQueryTooLong', () => {
                const keyword = query.keyword || readKeywordFromUrl();
                if (!keyword) {
                    return;
                }
                setBulkSearchDialogState(keywordToBulkSearchValues(keyword));
            });
        } catch {
            return undefined;
        }
    }, [query.keyword]);

    return (
        <SearchFilterBar
            keyword={query.keyword ?? ''}
            onKeywordChange={handleKeywordChange}
            activeFilters={activeFilters}
            onSetActiveFilter={setActiveFilter}
            onRemoveFilter={removeFilter}
            onApplyBulkSearch={applyBulkSearch}
            bulkSearchDialogState={bulkSearchDialogState}
            onBulkSearchDialogStateConsumed={handleDialogStateConsumed}
        />
    );
}
